import Title from "common/Typography/Title";
import {
  ConfigurationCard,
  SettingsPageWrapper,
} from "features/settings/common/styles";
import PaymentsTable from "features/settings/Billing/PaymentsTable";
import Tabs from "common/Tabs/Tabs";
import { LanguageNameSpaces, SubscriptionType } from "@shared/types";
import GroupSubscriptions from "features/settings/Billing/GroupSubscriptions";
import useTabsWithUrl from "common/Tabs/useTabsWithUrl";
import {
  BILLING_SETTINGS_PATH,
  MARKETPLACE_PAYMENTS_FEATURE_FLAG,
} from "common/constants";
import { BillingTab } from "features/settings/common/types";
import {
  MAX_SETTINGS_WIDTH,
  TAB_TO_DISPLAY,
} from "features/settings/common/constants";
import styled, { css, useTheme } from "styled-components";
import NewLookBillingHistory from "features/settings/Billing/NewLookBillingHistory.tsx";
import { useSelector } from "react-redux";
import { useContext, useEffect } from "react";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import CornixBillingPage from "features/settings/Billing/CornixBillingPage";
import { sharedLib } from "@shared/services/shared.service";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const Billing = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { analytics } = useContext(AnalyticsContext);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const { isNewLook } = useTheme();
  const { tabDisplayName, onChange: onTabChange } = useTabsWithUrl<BillingTab>({
    tabToDisplayName: TAB_TO_DISPLAY,
    tabs: Object.values(BillingTab),
    path: BILLING_SETTINGS_PATH,
    defaultTab: BillingTab.CORNIX,
  });

  useEffect(() => {
    analytics?.settingsBillingView({});
  }, [analytics]);

  const getPaymentsTable = (subscriptionType: SubscriptionType) => {
    if (isNewLook) {
      return <NewLookBillingHistory subscriptionType={subscriptionType} />;
    }
    return (
      <ConfigurationCard style={{ marginTop: 16 }}>
        <Title level={3} style={{ marginBottom: 16 }}>
          {t("billing_history_title")}
        </Title>
        <PaymentsTable subscriptionType={subscriptionType} />
      </ConfigurationCard>
    );
  };

  const getContent = () => {
    if (user?.hasFeatureFlag(MARKETPLACE_PAYMENTS_FEATURE_FLAG)) {
      return (
        <StyledTabs
          activeKey={tabDisplayName}
          onChange={onTabChange}
          items={[
            {
              label: t(TAB_TO_DISPLAY[BillingTab.CORNIX]),
              key: TAB_TO_DISPLAY[BillingTab.CORNIX],
              children: isNewLook ? (
                <NewLookTabStack>
                  <CornixBillingPage />
                </NewLookTabStack>
              ) : (
                <CornixBillingPage />
              ),
            },
            {
              label: t(TAB_TO_DISPLAY[BillingTab.GROUPS]),
              key: TAB_TO_DISPLAY[BillingTab.GROUPS],
              children: isNewLook ? (
                <NewLookTabStack>
                  <GroupSubscriptions />
                  {getPaymentsTable(SubscriptionType.GROUP)}
                </NewLookTabStack>
              ) : (
                <>
                  <GroupSubscriptions />
                  {getPaymentsTable(SubscriptionType.GROUP)}
                </>
              ),
            },
          ]}
        />
      );
    }
    return <CornixBillingPage />;
  };

  return <SettingsPageWrapper>{getContent()}</SettingsPageWrapper>;
};

export default Billing;

const StyledTabs = styled(Tabs)`
  padding: 0;
  width: 100%;
  max-width: ${MAX_SETTINGS_WIDTH}px;
  .ant-tabs-tab {
    padding: 4px 0 12px 0;
  }
  .ant-tabs-nav {
    margin-bottom: 0;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      max-width: none;
      .ant-tabs-nav {
        margin-bottom: 16px;
      }
      .ant-tabs-tab {
        padding: 6px 0 12px;
        font-size: 14px;
      }
      .ant-tabs-tab + .ant-tabs-tab {
        margin-left: 24px;
      }
    `}
`;
/** The sections of a billing tab, one gap apart (new look). */
const NewLookTabStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
